import { Form, Input } from "antd";
import { useState } from "react";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { passwordValidator } from "../../utils/passwordValidator";
import { InputLoading } from "./InputLoading";

const CustomInput = ({
  label,
  name,
  type,
  defaultValue,
  errorMessage,
  required,
  isEmail,
  disable,
  rules = [],
  dependencies,
  onChange,
  noValidation,
  loading,
  prefix,
  postfix,
  integerOnly,
  ...rest
}) => {
  const [show, setShow] = useState(false);
  const form = Form.useFormInstance(); // Get form instance

  // Handle input change with sanitization (blocks typing and pasting)
  const handleInputChange = (e) => {
    // Sanitize the value to remove restricted characters
    const sanitizedValue = e.target.value.replace(/['""`''""]/g, "");

    // Update the actual input value
    e.target.value = sanitizedValue;

    // Update the form field value
    if (form && name) {
      form.setFieldValue(name, sanitizedValue);
    }

    // Handle password confirmation validation
    if (name === "confirmPassword") {
      const newPassword = form.getFieldValue("password");
      if (sanitizedValue && newPassword !== sanitizedValue) {
        form.setFields([
          {
            name: "confirmPassword",
            errors: ["Passwords do not match!"],
          },
        ]);
      } else {
        form.setFields([
          {
            name: "confirmPassword",
            errors: [],
          },
        ]);
      }
    }

    // Create a new event object with sanitized value for onChange callback
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: sanitizedValue,
      },
    };

    onChange?.(syntheticEvent); // Call the passed onChange function
  };

  // Handle onChange for number input to prevent negative values
  const handleNumberChange = (e) => {
    const { value } = e.target;
    const isValid = integerOnly
      ? value === "" || /^(0|[1-9]\d*)$/.test(value) // positive whole numbers only, no 0
      : value === "" || Number(value) >= 0;
    if (isValid) {
      handleInputChange(e);
    }
  };

  // Custom validation rule for email input
  const emailValidationRule = isEmail
    ? {
        type: "email",
        message: "Please enter a valid email address",
      }
    : null;

  // Default rules (can be overridden by parent)
  const defaultRules = [
    {
      required: required,
      message: `${label} is required`,
    },
    ...(emailValidationRule ? [emailValidationRule] : []),
    ...((name === "password" || name === "Password") && !noValidation
      ? [
          {
            validator: (_, value) => {
              if (!value || passwordValidator(value)) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  "Password must be at least 8 characters long and include a combination of uppercase and lowercase letters, numbers, and special characters (e.g. !, @, #, $, %)",
                ),
              );
            },
          },
        ]
      : []),
  ];
  // Show skeleton loader when loading
  if (loading) {
    return (
      <div>
        {label && (
          <label className="inline-flex w-full text-sm text-black form-label flex pl-0.5 py-0.5">
            {label}
            {required && <span className="text-red-500">*</span>}
          </label>
        )}
        <InputLoading />
      </div>
    );
  }
  return (
    <Form.Item
      label={
        required ? (
          <span>
            {label}
            <span style={{ color: "red" }}>*</span>
          </span>
        ) : (
          label
        )
      }
      name={name}
      dependencies={dependencies}
      initialValue={defaultValue}
      rules={rules.length ? rules : defaultRules}
      validateStatus={errorMessage ? "error" : undefined}
      help={errorMessage}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      required={false}
    >
      {type === "password" ? (
        <Input.Password
          type={show ? "text" : "password"}
          autoComplete="off"
          spellCheck="false"
          onChange={handleInputChange}
          iconRender={(visible) =>
            visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
          }
          {...rest}
        />
      ) : type === "number" ? (
        <Input
          disabled={disable}
          type={type}
          inputMode={integerOnly ? "numeric" : undefined}
          step={integerOnly ? 1 : undefined}
          defaultValue={defaultValue}
          onChange={handleNumberChange}
          min={0}
          prefix={prefix}
          suffix={postfix}
          style={{
            color: disable ? "#000000" : "initial",
            height: "32px",
          }}
          {...rest}
          onKeyDown={(e) => {
            if (integerOnly) {
              // Allow: digits, and control/navigation keys (Backspace, Delete, Tab,
              // arrows, Home/End) plus Ctrl/Cmd combos for copy/paste/select-all
              const allowedControlKeys = [
                "Backspace",
                "Delete",
                "Tab",
                "Enter",
                "Escape",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "ArrowDown",
                "Home",
                "End",
              ];
              const isDigit = /^[0-9]$/.test(e.key);
              const isAllowedControl = allowedControlKeys.includes(e.key);
              const isCtrlCombo = e.ctrlKey || e.metaKey; // allow Ctrl+A, Ctrl+C, Ctrl+V etc.

              if (!isDigit && !isAllowedControl && !isCtrlCombo) {
                e.preventDefault();
                return;
              }

              if (isDigit) {
                const input = e.target;
                const { selectionStart, selectionEnd, value } = input;
                const nextValue =
                  value.slice(0, selectionStart) +
                  e.key +
                  value.slice(selectionEnd);

                if (!/^(0|[1-9]\d*)$/.test(nextValue)) {
                  e.preventDefault();
                }
              }
              return;
            }

            // non-integerOnly path (existing behavior)
            const blockedKeys = ["e", "E", "+", "-"];
            if (blockedKeys.includes(e.key)) {
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            const pastedText = e.clipboardData.getData("text");
            const isInvalid = integerOnly
              ? !/^(0|[1-9]\d*)$/.test(pastedText)
              : pastedText.includes("-") ||
                pastedText.includes("e") ||
                pastedText.includes("E");
            if (isInvalid) {
              e.preventDefault();
            }
          }}
        />
      ) : (
        <Input
          disabled={disable}
          type={type}
          onChange={handleInputChange}
          className={`rounded-[4px] ${disable ? "!bg-[#fafbfc]" : ""}`}
          prefix={prefix}
          style={{
            color: disable ? "#000000" : "initial",
            height: "32px",
          }}
          {...rest}
        />
      )}
    </Form.Item>
  );
};

export default CustomInput;
